
'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Key, Lock, LockOpen, FileInput, Eye, EyeOff, Loader2, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { aesEncryptContent } from '../../data/aes-encrypt-content';

declare global {
    interface Window {
        crypto: Crypto & {
            subtle: SubtleCrypto;
            getRandomValues: <T extends ArrayBufferView | null>(array: T) => T;
        };
    }
}

type Operation = 'encrypt' | 'decrypt';

// Generate a random encryption key
async function generateKey(): Promise<CryptoKey> {
    if (typeof window === 'undefined') {
        throw new Error('Crypto operations are only available in the browser');
    }
    return window.crypto.subtle.generateKey(
        {
            name: 'AES-GCM', // Using AES-GCM for authenticated encryption
            length: 256, // 256-bit key
        },
        true, // extractable
        ['encrypt', 'decrypt']
    );
}

// Convert ArrayBuffer or ArrayBufferView to base64
function arrayBufferToBase64(buffer: ArrayBuffer | ArrayBufferView): string {
    const bytes = buffer instanceof ArrayBuffer
        ? new Uint8Array(buffer)
        : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

// Convert base64 to ArrayBuffer
function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

export default function AesEncrypt() {
    const { toast } = useToast();
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [password, setPassword] = useState('');
    const [operation, setOperation] = useState<Operation>('encrypt');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [autoGenerateKey, setAutoGenerateKey] = useState(true);
    const [encryptionKey, setEncryptionKey] = useState('');
    const [iv, setIv] = useState<Uint8Array | null>(null);
    const [salt, setSalt] = useState<Uint8Array | null>(null);
    // Using the imported toast directly

    // Generate a random IV (Initialization Vector)
    const generateIv = useCallback((): Uint8Array => {
        if (typeof window === 'undefined') {
            throw new Error('Crypto operations are only available in the browser');
        }
        const iv = new Uint8Array(12);
        window.crypto.getRandomValues(iv);
        return iv;
    }, []);

    // Generate a random salt
    const generateSalt = useCallback((): Uint8Array => {
        if (typeof window === 'undefined') {
            throw new Error('Crypto operations are only available in the browser');
        }
        const salt = new Uint8Array(16);
        window.crypto.getRandomValues(salt);
        return salt;
    }, []);

    // Derive a key from a password using PBKDF2
    const deriveKey = useCallback(async (password: string, salt: Uint8Array | ArrayBuffer | string | null): Promise<CryptoKey> => {
        if (typeof window === 'undefined') {
            throw new Error('Crypto operations are only available in the browser');
        }
        const encoder = new TextEncoder();
        const keyMaterial = await window.crypto.subtle.importKey(
            'raw',
            encoder.encode(password),
            { name: 'PBKDF2' },
            false,
            ['deriveBits', 'deriveKey']
        );

        // Ensure salt is a proper Uint8Array
        let saltBuffer: ArrayBuffer;
        if (salt === null) {
            // If salt is null, generate a new random salt
            const newSalt = generateSalt();
            setSalt(newSalt);
            saltBuffer = new Uint8Array(newSalt).buffer.slice(0);
        } else if (salt instanceof Uint8Array) {
            // Create a new ArrayBuffer from the Uint8Array to ensure it's not a SharedArrayBuffer
            saltBuffer = new Uint8Array(salt).buffer.slice(0);
        } else if (salt instanceof ArrayBuffer) {
            // If it's already an ArrayBuffer, ensure it's not a SharedArrayBuffer
            saltBuffer = new Uint8Array(salt).buffer.slice(0);
        } else if (typeof salt === 'string') {
            // If salt is a string, encode it and get the buffer
            saltBuffer = encoder.encode(salt).buffer.slice(0);
        } else {
            throw new Error('Invalid salt type');
        }

        return await window.crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: new Uint8Array(saltBuffer), // Ensure it's a proper Uint8Array
                iterations: 100000,
                hash: 'SHA-256',
            },
            keyMaterial,
            { name: 'AES-GCM', length: 256 },
            true,
            ['encrypt', 'decrypt']
        );
    }, []);

    // Encrypt text using AES-GCM
    const encrypt = useCallback(async (text: string, password: string): Promise<string> => {
        try {
            const salt = generateSalt();
            const iv = generateIv();
            const key = await deriveKey(password, salt);
            const encoder = new TextEncoder();
            const data = encoder.encode(text);

            const encryptedData = await window.crypto.subtle.encrypt(
                {
                    name: 'AES-GCM',
                    iv: new Uint8Array(iv),
                },
                key,
                data.buffer
            );

            // Combine salt, iv, and encrypted data
            const result = {
                salt: arrayBufferToBase64(salt.buffer as ArrayBuffer),
                iv: arrayBufferToBase64(iv.buffer as ArrayBuffer),
                data: arrayBufferToBase64(encryptedData),
            };

            return JSON.stringify(result);
        } catch (error) {
            console.error('Encryption error:', error);
            throw new Error('Encryption failed');
        }
    }, [deriveKey, generateIv, generateSalt]);

    // Decrypt text using AES-GCM
    const decrypt = useCallback(async (encryptedText: string, password: string): Promise<string> => {
        try {
            const { salt: saltBase64, iv: ivBase64, data: dataBase64 } = JSON.parse(encryptedText);

            const saltBuffer = new Uint8Array(base64ToArrayBuffer(saltBase64));
            const ivBuffer = new Uint8Array(base64ToArrayBuffer(ivBase64));
            const dataBuffer = base64ToArrayBuffer(dataBase64);

            const key = await deriveKey(password, saltBuffer);

            const decryptedData = await window.crypto.subtle.decrypt(
                {
                    name: 'AES-GCM',
                    iv: ivBuffer,
                },
                key,
                dataBuffer
            );

            return new TextDecoder().decode(decryptedData);
        } catch (error) {
            console.error('Decryption error:', error);
            throw new Error('Decryption failed. Invalid password or data.');
        }
    }, [deriveKey]);

    // Handle encryption/decryption
    const processText = useCallback(async () => {
        if (!inputText.trim() || !password) {
            setOutputText('');
            return;
        }

        setIsLoading(true);

        try {
            if (operation === 'encrypt') {
                const result = await encrypt(inputText, password);
                setOutputText(result);

                // Set the encryption key and IV for reference
                const { salt, iv } = JSON.parse(result);
                setSalt(salt);
                setIv(iv);

                toast({
                    title: 'Encryption successful',
                    description: 'Your text has been encrypted.',
                    variant: 'success'
                });
            } else {
                const result = await decrypt(inputText, password);
                setOutputText(result);

                toast({
                    title: 'Decryption successful',
                    description: 'Your text has been decrypted.',
                    variant: 'success'
                });
            }
        } catch (error) {
            console.error('Processing error:', error);
            toast({
                title: 'Error',
                description: error instanceof Error ? error.message : 'An error occurred',
                variant: 'destructive'
            });
            setOutputText('');
        } finally {
            setIsLoading(false);
        }
    }, [inputText, password, operation, encrypt, decrypt]);

    // Auto-process when inputs change
    useEffect(() => {
        if (inputText && password) {
            processText();
        } else {
            setOutputText('');
        }
    }, [inputText, password, operation, processText]);

    // Generate a new encryption key
    const handleGenerateKey = useCallback(async () => {
        try {
            const key = await generateKey();
            const exportedKey = await window.crypto.subtle.exportKey('raw', key);
            const keyString = arrayBufferToBase64(exportedKey);
            setEncryptionKey(keyString);
            setPassword(keyString);
        } catch (error) {
            console.error('Key generation error:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate encryption key',
                variant: 'destructive'
            });
        }
    }, []);

    // Generate a key on component mount if auto-generate is enabled
    useEffect(() => {
        if (autoGenerateKey) {
            handleGenerateKey();
        }
    }, [autoGenerateKey, handleGenerateKey]);

    const copyToClipboard = (text: string | Uint8Array) => {
        const textToCopy = text instanceof Uint8Array ? arrayBufferToBase64(text) : text;
        navigator.clipboard.writeText(textToCopy);
        toast({
            title: 'Success',
            description: 'Text copied to clipboard!',
            variant: 'success'
        });
    };

    const clearAll = () => {
        setInputText('');
        setOutputText('');
        setPassword('');
        setEncryptionKey('');
        setIv(null);
        setSalt(null);
    };

    return (

        <div className="container mx-auto px-4 py-8">
            <div className="mb-8">
                <ToolPageIntro title="AES Encryption Tool">
                Encrypt and decrypt data using AES (Advanced Encryption Standard). Free online AES encryption with multiple key sizes.
            </ToolPageIntro>
            </div>

            <div className="grid grid-cols-1 gap-8">
                {/* Operation Tabs */}
                <Card>
                    <CardHeader>
                        <CardTitle>Operation</CardTitle>
                        <CardDescription>Choose to encrypt or decrypt your text</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Tabs
                            value={operation}
                            onValueChange={(value) => setOperation(value as Operation)}
                            className="w-full"
                        >
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="encrypt" className="flex items-center">
                                    <Lock className="h-4 w-4 mr-2" /> Encrypt
                                </TabsTrigger>
                                <TabsTrigger value="decrypt" className="flex items-center">
                                    <LockOpen className="h-4 w-4 mr-2" /> Decrypt
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </CardContent>
                </Card>

                {/* Password/Key Section */}
                <Card>
                    <CardHeader>
                        <CardTitle>Encryption Key</CardTitle>
                        <CardDescription>
                            {operation === 'encrypt'
                                ? 'Enter a secure password or generate a key'
                                : 'Enter the password or key used for encryption'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <Label htmlFor="password">
                                    {operation === 'encrypt' ? 'Password/Key' : 'Decryption Key'}
                                </Label>
                                <div className="flex items-center space-x-4">
                                    {operation === 'encrypt' && (
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                id="auto-generate"
                                                checked={autoGenerateKey}
                                                onCheckedChange={setAutoGenerateKey}
                                            />
                                            <Label htmlFor="auto-generate">Auto-generate</Label>
                                        </div>
                                    )}
                                    {!autoGenerateKey && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleGenerateKey}
                                            disabled={isLoading}
                                        >
                                            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                                            Generate Key
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Key className="h-4 w-4 text-muted-foreground" />
                                </div>
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={
                                        operation === 'encrypt'
                                            ? 'Enter a secure password or use generated key'
                                            : 'Enter the decryption key or password'
                                    }
                                    className="pl-10 pr-10"
                                    disabled={autoGenerateKey && operation === 'encrypt'}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:bg-transparent"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                            {operation === 'encrypt' && autoGenerateKey && (
                                <p className="text-xs text-muted-foreground mt-1">
                                    A secure key has been generated for you. Save this key to decrypt later.
                                </p>
                            )}
                        </div>

                        {operation === 'encrypt' && (
                            <div className="space-y-4 pt-4 border-t">
                                <div className="space-y-2">
                                    <Label>Encryption Details</Label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Salt</Label>
                                            <div className="flex items-center">
                                                <Input
                                                    value={salt ? arrayBufferToBase64(new Uint8Array(salt).buffer as ArrayBuffer) : 'N/A'}
                                                    readOnly
                                                    className="h-8 text-xs font-mono"
                                                    placeholder="Will be generated on encryption..."
                                                />
                                                {salt && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 ml-1"
                                                        onClick={() => copyToClipboard(salt)}
                                                    >
                                                        <Copy className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">IV (Initialization Vector)</Label>
                                            <div className="flex items-center">
                                                <Input
                                                    value={iv ? (typeof iv === 'string' ? iv : arrayBufferToBase64(iv)) : 'N/A'}
                                                    readOnly
                                                    className="h-8 text-xs font-mono"
                                                    placeholder="Will be generated on encryption..."
                                                />
                                                {iv && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 ml-1"
                                                        onClick={() => copyToClipboard(iv)}
                                                    >
                                                        <Copy className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Input & Output Sections */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Input Section */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <Label htmlFor="input-text">
                                {operation === 'encrypt' ? 'Text to Encrypt' : 'Encrypted Data'}
                            </Label>
                            <div className="flex items-center space-x-2">
                                {inputText && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => copyToClipboard(inputText)}
                                        title="Copy to clipboard"
                                        disabled={isLoading}
                                    >
                                        <Copy className="h-4 w-4 mr-1" /> Copy
                                    </Button>
                                )}
                                {inputText && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={clearAll}
                                        title="Clear all"
                                        disabled={isLoading}
                                    >
                                        <span>Clear</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="relative">
                            <Textarea
                                id="input-text"
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                placeholder={
                                    operation === 'encrypt'
                                        ? 'Enter the text you want to encrypt...'
                                        : 'Paste the encrypted data (JSON format) to decrypt...'
                                }
                                className="min-h-[200px] font-mono text-sm"
                                disabled={isLoading}
                            />
                            {isLoading && (
                                <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Output Section */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <Label htmlFor="output-text">
                                {operation === 'encrypt' ? 'Encrypted Result' : 'Decrypted Text'}
                            </Label>
                            {outputText && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => copyToClipboard(outputText)}
                                    title="Copy to clipboard"
                                    disabled={isLoading || !outputText}
                                >
                                    <Copy className="h-4 w-4 mr-1" /> Copy
                                </Button>
                            )}
                        </div>
                        <div className="relative">
                            <Textarea
                                id="output-text"
                                value={outputText}
                                readOnly
                                placeholder={
                                    operation === 'encrypt'
                                        ? 'Your encrypted data will appear here...'
                                        : 'Decrypted text will appear here...'
                                }
                                className="min-h-[200px] font-mono text-sm bg-muted/50"
                            />
                            {isLoading && (
                                <div className="absolute inset-0 bg-muted/30 flex items-center justify-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* About This Tool */}
            <div className="mt-16 bg-muted/50 p-6 rounded-lg">
                <h3 className="text-lg font-medium mb-3">About This Tool</h3>
                <p className="mb-3">
                    The AES Encryption & Decryption tool provides secure encryption using the AES-256-GCM algorithm.
                    Your data is encrypted in the browser and never leaves your device.
                </p>
                <ul className="space-y-2 text-muted-foreground list-disc pl-5 mb-4">
                    <li><span className="text-foreground">Secure Encryption</span> - Uses AES-256-GCM with PBKDF2 key derivation</li>
                    <li><span className="text-foreground">No Data Stored</span> - All processing happens in your browser</li>
                    <li><span className="text-foreground">Key Generation</span> - Automatically generates secure encryption keys</li>
                    <li><span className="text-foreground">Format Preserving</span> - Maintains text formatting during encryption/decryption</li>
                </ul>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                                <strong>Important:</strong> Save your encryption key, salt, and IV if you want to decrypt the data later.
                                Without these, your data cannot be recovered.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* SEO CONTENT */}
            <div className="mt-16">
                <InformationalContent
                    title={aesEncryptContent.title}
                    description={aesEncryptContent.description}
                    content={aesEncryptContent.content}
                    faqs={aesEncryptContent.faqs}
                    relatedTools={aesEncryptContent.relatedTools}
                />
            </div>
        </div>
    );
}
